/**
 * RetentionByPeriodWidget - Ретеншен N-го дня по юнитам регистрации
 * ====================================================================
 *
 * Ось X — период регистрации когорты (дни / недели / месяцы),
 * ось Y — % удержания на выбранных днях N (R1, R3, R7, ... + произвольный N).
 * Отдельный график на каждую платформу.
 *
 * Поверх каждой серии можно наложить линию тренда (линейная / степенная) —
 * считается на клиенте по уже загруженным точкам, сеть не дёргается.
 *
 * Пресетные R запрашиваются с сервера все сразу — переключение кнопок под
 * графиком не дёргает сеть. Перезапрос только при смене гранулярности
 * или добавлении своего N.
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { RetentionGeoFilter } from './RetentionGeoFilter';
import { useRetentionGeo } from '../../hooks/useRetentionGeo';
import { getPlatformLabel, sortPlatforms } from '../../config/platforms';
import { retention as retentionSpecs } from '@ggstat/chart-specs';
import { getPlatformIcon } from '../ui/brand-icons';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Calendar, Plus, X, TrendingUp } from 'lucide-react';
import { fitTrend } from '../../utils/trendline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';

// Дни, палитра и модели тренда — shared/chart-specs/specs/retention.json
// (их же рисует MCP-сервер)
const {
  presetDays: PRESET_DAYS,
  defaultSelectedDays: DEFAULT_SELECTED_DAYS,
  minDay: MIN_DAY,
  maxDay: MAX_DAY,
  dayColors: DAY_COLORS,
  trendModels: TREND_MODELS
} = retentionSpecs.byPeriod;

function getDayColor(day, allDays) {
  const index = allDays.indexOf(day);
  return DAY_COLORS[(index < 0 ? 0 : index) % DAY_COLORS.length];
}

function formatPeriod(value, granularity) {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;

  if (granularity === 'month') {
    return date.toLocaleDateString('ru-RU', { month: '2-digit', year: 'numeric' });
  }
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

export function RetentionByPeriodWidget() {
  const [granularity, setGranularity] = useState('day');
  const [customDays, setCustomDays] = useState([]);
  const [selectedDays, setSelectedDays] = useState(DEFAULT_SELECTED_DAYS);
  const [customInput, setCustomInput] = useState('');
  const [inputError, setInputError] = useState(null);
  const [trendModels, setTrendModels] = useState([]);
  const { geos, geoKey, toggleGeo, clearGeos } = useRetentionGeo();

  // Все дни, которые тянем с сервера: пресеты + добавленные вручную.
  // Ключ зависимостей — строка, чтобы не перезапрашивать на каждый рендер.
  const requestedDays = useMemo(
    () => [...new Set([...PRESET_DAYS, ...customDays])].sort((a, b) => a - b),
    [customDays]
  );
  const requestedDaysKey = requestedDays.join(',');

  const widgetData = useWidgetData(
    async (filters, options) => {
      return MetricsService.getRetentionByPeriod(
        { ...filters, geo: geos },
        granularity,
        requestedDays,
        options
      );
    },
    [granularity, requestedDaysKey, geoKey]
  );

  const widgetProps = useBaseWidget(widgetData);
  const { data: rows } = widgetProps;

  const visibleDays = useMemo(
    () => [...selectedDays].sort((a, b) => a - b),
    [selectedDays]
  );

  // Плоские строки { platform, period, cohortSize, retention } → по графику
  // на платформу, точки в широком формате { period, cohortSize, d1, d7, ... }
  const sections = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const byPlatform = new Map();

    list.forEach((row) => {
      const platform = row.platform || 'unknown';
      const point = {
        period: row.period,
        periodLabel: formatPeriod(row.period, granularity),
        cohortSize: row.cohortSize
      };

      requestedDays.forEach((day) => {
        const value = row.retention ? row.retention[String(day)] : null;
        point[`d${day}`] = value === undefined ? null : value;
      });

      const bucket = byPlatform.get(platform) || [];
      bucket.push(point);
      byPlatform.set(platform, bucket);
    });

    return sortPlatforms([...byPlatform.keys()]).map((platform) => ({
      platform,
      points: byPlatform.get(platform).sort((a, b) => a.period.localeCompare(b.period))
    }));
  }, [rows, granularity, requestedDays]);

  // Тренды считаем поверх готовых точек: X — порядковый номер периода (1..n),
  // чтобы пропуски в серии не сдвигали шкалу.
  const trendedSections = useMemo(() => {
    if (trendModels.length === 0) return sections;

    return sections.map((section) => {
      const meta = {};
      const fits = [];

      visibleDays.forEach((day) => {
        const samples = section.points
          .map((point, index) => ({ x: index + 1, y: point[`d${day}`] }))
          .filter((sample) => sample.y !== null && sample.y !== undefined);

        trendModels.forEach((model) => {
          const fit = fitTrend(model, samples);
          if (!fit) return;
          const key = `t_${model}_d${day}`;
          meta[key] = { day, model, r2: fit.r2 };
          fits.push({ key, predict: fit.predict });
        });
      });

      if (fits.length === 0) return { ...section, trendMeta: meta, trendKeys: [] };

      const points = section.points.map((point, index) => {
        const next = { ...point };
        fits.forEach(({ key, predict }) => {
          const value = predict(index + 1);
          next[key] = Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
        });
        return next;
      });

      return { ...section, points, trendMeta: meta, trendKeys: fits.map((f) => f.key) };
    });
  }, [sections, visibleDays, trendModels]);

  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((value) => value !== day) : [...prev, day]
    );
  };

  const addCustomDay = () => {
    const parsed = parseInt(customInput, 10);

    if (!Number.isFinite(parsed) || parsed < MIN_DAY || parsed > MAX_DAY) {
      setInputError(`Введите число от ${MIN_DAY} до ${MAX_DAY}`);
      return;
    }

    setInputError(null);
    setCustomInput('');
    if (!PRESET_DAYS.includes(parsed) && !customDays.includes(parsed)) {
      setCustomDays((prev) => [...prev, parsed].sort((a, b) => a - b));
    }
    setSelectedDays((prev) => (prev.includes(parsed) ? prev : [...prev, parsed]));
  };

  const removeCustomDay = (day) => {
    setCustomDays((prev) => prev.filter((value) => value !== day));
    setSelectedDays((prev) => prev.filter((value) => value !== day));
  };

  const CustomTooltip = ({ active, payload, label, trendMeta }) => {
    if (!active || !payload || payload.length === 0) return null;
    const cohortSize = payload[0]?.payload?.cohortSize;

    return (
      <div className="bg-background border rounded-lg shadow-lg p-3">
        <p className="font-medium mb-1">{label}</p>
        {cohortSize !== undefined && (
          <p className="text-xs text-muted-foreground mb-2">
            Когорта: {Number(cohortSize).toLocaleString('ru-RU')}
          </p>
        )}
        {payload.map((entry) => {
          const meta = trendMeta ? trendMeta[entry.dataKey] : null;
          return (
            <p key={entry.dataKey} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value === null || entry.value === undefined ? '—' : `${entry.value}%`}
              {meta && ` · R² ${meta.r2.toFixed(2)}`}
            </p>
          );
        })}
      </div>
    );
  };

  const granularityToggle = (
    <ToggleGroup
      type="single"
      value={granularity}
      onValueChange={(value) => value && setGranularity(value)}
      size="sm"
    >
      <ToggleGroupItem value="day" aria-label="По дням"><Calendar className="h-3 w-3 mr-1" />Дни</ToggleGroupItem>
      <ToggleGroupItem value="week" aria-label="По неделям">Недели</ToggleGroupItem>
      <ToggleGroupItem value="month" aria-label="По месяцам">Месяцы</ToggleGroupItem>
    </ToggleGroup>
  );

  return (
    <div className="space-y-3">
      <BaseWidget
        {...widgetProps}
        title="Ретеншен N-го дня"
        subtitle="По оси X — период регистрации когорты, по оси Y — % удержания на выбранных днях. Точка RN появляется, когда вся когорта периода прожила N дней"
        emptyMessage="Нет данных за выбранный период"
        skeletonRows={8}
        headerAction={
          <div className="flex items-center gap-2">
            <RetentionGeoFilter geos={geos} onToggleGeo={toggleGeo} onClearGeos={clearGeos} />
            {granularityToggle}
          </div>
        }
      >
        <div className="space-y-6">
          {trendedSections.map((section) => (
            <div key={section.platform} className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                {getPlatformIcon(section.platform, 16)}
                <span>{getPlatformLabel(section.platform)}</span>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={section.points} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="periodLabel"
                    tick={{ fill: 'currentColor' }}
                    className="text-xs"
                    minTickGap={16}
                  />
                  <YAxis
                    domain={[0, 'auto']}
                    tick={{ fill: 'currentColor' }}
                    className="text-xs"
                    allowDecimals={false}
                    label={{ value: '% удержания', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<CustomTooltip trendMeta={section.trendMeta} />} />
                  <Legend />
                  {visibleDays.map((day) => (
                    <Line
                      key={day}
                      type="monotone"
                      dataKey={`d${day}`}
                      name={`R${day}`}
                      stroke={getDayColor(day, requestedDays)}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                  ))}
                  {visibleDays.map((day) =>
                    TREND_MODELS.filter((model) => trendModels.includes(model.id)).map((model) => {
                      const key = `t_${model.id}_d${day}`;
                      if (!section.trendMeta || !section.trendMeta[key]) return null;
                      return (
                        <Line
                          key={key}
                          type="linear"
                          dataKey={key}
                          name={`R${day} тренд (${model.short})`}
                          stroke={getDayColor(day, requestedDays)}
                          strokeWidth={1.5}
                          strokeDasharray={model.dash}
                          dot={false}
                          activeDot={false}
                          legendType="none"
                          isAnimationActive={false}
                          connectNulls
                        />
                      );
                    })
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      </BaseWidget>

      <div className="rounded-md border p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground mr-1">Показать:</span>
          {PRESET_DAYS.map((day) => (
            <Button
              key={day}
              type="button"
              size="sm"
              variant={selectedDays.includes(day) ? 'default' : 'outline'}
              onClick={() => toggleDay(day)}
              className="h-8 px-3"
            >
              R{day}
            </Button>
          ))}
          {customDays.map((day) => (
            <div key={day} className="flex items-center">
              <Button
                type="button"
                size="sm"
                variant={selectedDays.includes(day) ? 'default' : 'outline'}
                onClick={() => toggleDay(day)}
                className="h-8 px-3 rounded-r-none"
              >
                R{day}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => removeCustomDay(day)}
                className="h-8 px-2 rounded-l-none border-l-0"
                aria-label={`Убрать R${day}`}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground mr-1">Тренд:</span>
          <ToggleGroup
            type="multiple"
            value={trendModels}
            onValueChange={setTrendModels}
            size="sm"
          >
            {TREND_MODELS.map((model) => (
              <ToggleGroupItem key={model.id} value={model.id} aria-label={model.label}>
                <TrendingUp className="h-3 w-3 mr-1" />
                {model.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <span className="text-xs text-muted-foreground">
            считается по видимым сериям, R² — в подсказке точки
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Свой день N:</span>
          <Input
            type="number"
            min={MIN_DAY}
            max={MAX_DAY}
            value={customInput}
            onChange={(event) => {
              setCustomInput(event.target.value);
              setInputError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addCustomDay();
              }
            }}
            placeholder="например, 45"
            className="h-8 w-32"
          />
          <Button type="button" size="sm" variant="outline" onClick={addCustomDay} className="h-8">
            <Plus className="h-3 w-3 mr-1" />
            Добавить
          </Button>
          {inputError && <span className="text-sm text-destructive">{inputError}</span>}
        </div>
      </div>
    </div>
  );
}

export default RetentionByPeriodWidget;
